import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { careerAssetId } from '@bc-library/recruitment'
import type { TJobBoardChannel, TJobBoardChannelKey, TJobBoardConnection, TJobBoardPosting, TJobBoardPostingAction, TJobBoardSchema } from '@bc-library/recruitment'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'jobBoards'

export type TJobBoardChannelState = TJobBoardChannel & {
  /** The board is open to the firm (API catalog config/integrations.json, or the server's JOB_BOARDS_READY) */
  isReady: boolean
  /** Site of the firm's list that stands for the board (null until the board is used) */
  website: null | {
    _id: string
    display: string
    /** The firm turned the board on: feed served, or API posting on */
    feedEnabled: boolean
    /** Values of the board's firm fields (TJobBoardSchema.firmFields), keyed by field */
    settings: Record<string, unknown> | null
  }
  /** Only for the boards posted through their API (postsByApi) */
  connection: TJobBoardConnection | null
  /** Open offers of the career site not ticked for the board yet, what `applyToOpenOffers` adds */
  offersToAdd?: number
}

/** Address of a career site image: an uploaded image (`asset:<id>`) is served by the API, a link stays as is */
export const careerImageSrc = (value: string | null | undefined) => {
  const assetId = careerAssetId(value)
  return assetId ? `${BASE_URL}/${API_MAIN_VERSION}/${resource}/careerAssets/${assetId}` : value || null
}

/** An image uploaded for the career site, to save as `ref` (`asset:<id>`) */
export type TCareerImageUpload = { _id: string, ref: string, width: number, height: number }

/** Public feed of the offers ticked for a board, read by the board itself */
export const getJobBoardFeedUrl = (firmId: string, channel: TJobBoardChannelKey, format: 'json' | 'xml' = 'xml') =>
  `${BASE_URL}/${API_MAIN_VERSION}/${resource}/feed/${firmId}/${channel}${format === 'xml' ? '?format=xml' : ''}`

const listChannels = (axios: IFactoryFunctionRequest<TJobBoardChannelState[], undefined>) => (): IFactoryResponse<TJobBoardChannelState[], undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/channels`,
  })
}

type TUpdateChannelBody = {
  feedEnabled?: boolean
  applyToOpenOffers?: boolean
  /** Values of the board's firm fields; 400 { customMessage, field? } on an invalid one */
  settings?: Record<string, unknown>
}
type TUpdateChannelResponse = TJobBoardChannelState & { addedOffers: number }

const updateChannel = (axios: IFactoryFunctionRequest<TUpdateChannelResponse, TUpdateChannelBody>) =>
  (channel: TJobBoardChannelKey, data: TUpdateChannelBody): IFactoryResponse<TUpdateChannelResponse, TUpdateChannelBody> => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/channels/${channel}`,
      data,
    })
  }

/** code: an error code (AUTH...) or an issue code (notConnected, fieldRequired with its `field`) */
export type TTestChannelResponse = { ok: boolean, code?: string, field?: string, message?: string }

const testChannel = (axios: IFactoryFunctionRequest<TTestChannelResponse, undefined>) =>
  (channel: TJobBoardChannelKey): IFactoryResponse<TTestChannelResponse, undefined> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/channels/${channel}/test`,
    })
  }

/** Fields of a board posted through its API, labels and options in the requested language */
const getSchema = (axios: IFactoryFunctionRequest<TJobBoardSchema, undefined>) =>
  (channel: string, lang: string): IFactoryResponse<TJobBoardSchema, undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${channel}/schema`,
      query: { lang },
    })
  }

export type TJobBoardOptionItem = { value: string | number, label: string }
/** Cursor page of the options of a remote field (no search: the app searches the pages it read) */
export type TJobBoardOptionsPage = { items: TJobBoardOptionItem[], after: string | null, total: number | null }

const listOptions = (axios: IFactoryFunctionRequest<TJobBoardOptionsPage, undefined>) =>
  (channel: string, source: string, after?: string | null): IFactoryResponse<TJobBoardOptionsPage, undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${channel}/options/${source}`,
      query: after ? { after } : {},
    })
  }

const listOfferPostings = (axios: IFactoryFunctionRequest<TJobBoardPosting[], undefined>) =>
  (offerId: string): IFactoryResponse<TJobBoardPosting[], undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/offers/${offerId}/postings`,
    })
  }

export type TPostingAction = TJobBoardPostingAction
/** Publication types: options of the schema's publicationField */
export type TPostingActionBody = { publicationType?: string | number, scheduledOccurrences?: number, newType?: string | number }
type TPostingActionResponse = { postings: TJobBoardPosting[] }

/**
 * sync: creates or updates the job ad now (archives it when the offer should not be posted);
 * publish: new publication or renewal ({ publicationType, scheduledOccurrences? }, may use the firm's credits);
 * upgrade: { newType }; refresh: reads the status on the board; withdraw: unticks the board and archives the job ad
 */
const runPostingAction = (axios: IFactoryFunctionRequest<TPostingActionResponse, TPostingActionBody>) =>
  (offerId: string, channel: string, action: TPostingAction, data?: TPostingActionBody): IFactoryResponse<TPostingActionResponse, TPostingActionBody> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/offers/${offerId}/postings/${channel}/${action}`,
      data,
    })
  }

/** Image of the career site (FormData `file`): re-encoded by the API, served by careerImageSrc */
const uploadCareerImage = (axios: IFactoryFunctionRequest<TCareerImageUpload, FormData>) => (data: FormData): IFactoryResponse<TCareerImageUpload, FormData> => {
  return axios({
    method: EMethod.post,
    // FormData: the browser sets the multipart content type and its boundary
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/careerAssets`,
    data,
  })
}

/** Small copy of an image link (data URL), for the colours of a logo hosted on another site */
const careerImageThumbnail = (axios: IFactoryFunctionRequest<{ dataUrl: string }, undefined>) => (url: string): IFactoryResponse<{ dataUrl: string }, undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/careerAssets/thumbnail`,
    query: { url },
  })
}

export default (axios: AxiosInstance) => ({
  listChannels: listChannels(useAxios(axios)),
  updateChannel: updateChannel(useAxios(axios)),
  testChannel: testChannel(useAxios(axios)),
  getSchema: getSchema(useAxios(axios)),
  listOptions: listOptions(useAxios(axios)),
  listOfferPostings: listOfferPostings(useAxios(axios)),
  runPostingAction: runPostingAction(useAxios(axios)),
  uploadCareerImage: uploadCareerImage(useAxios(axios)),
  careerImageThumbnail: careerImageThumbnail(useAxios(axios)),
})
